import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Sparkles, Loader2, AlertCircle, CheckCircle2, Undo2 } from "lucide-react";

const API = `${window.location.origin}/api`;

/**
 * Iter 63+ — Hybrid material-cost extractor dialog.
 * Flow: Scan -> Preview table (editable supplier + selection) -> Confirm.
 * Also exposes a "Rollback" for easy re-runs on first-time users.
 */
export const ExtractMaterialsDialog = ({ open, onClose, onConfirmed, authHeaders }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isRolling, setIsRolling] = useState(false);
  const [preview, setPreview] = useState(null); // { total_invoices_scanned, material_proposals, ... }

  const handleScan = async () => {
    setIsScanning(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/materials/extract-from-invoices`, {}, authHeaders
      );
      setPreview(data);
      if (!data.material_proposals?.length) {
        toast.info("No material-cost line items detected in your invoices.");
      } else {
        toast.success(`Scanned ${data.total_invoices_scanned} invoices — found ${data.material_proposals.length} candidate material lines`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed");
    } finally {
      setIsScanning(false);
    }
  };

  const toggleSelected = (key) => {
    setPreview(prev => prev && {
      ...prev,
      material_proposals: prev.material_proposals.map(p =>
        `${p.invoice_id}:${p.line_index}` === key ? { ...p, selected: !p.selected } : p
      ),
    });
  };

  const updateSupplier = (key, supplier) => {
    setPreview(prev => prev && {
      ...prev,
      material_proposals: prev.material_proposals.map(p =>
        `${p.invoice_id}:${p.line_index}` === key ? { ...p, supplier } : p
      ),
    });
  };

  const selectedCount = preview?.material_proposals?.filter(p => p.selected).length || 0;
  const selectedValue = preview?.material_proposals
    ?.filter(p => p.selected)
    .reduce((sum, p) => sum + (Number(p.total) || 0), 0) || 0;

  const handleConfirm = async () => {
    if (!preview || selectedCount === 0) return;
    setIsConfirming(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/materials/extract-from-invoices/confirm`,
        { proposals: preview.material_proposals },
        authHeaders,
      );
      toast.success(`Added ${data.created} materials, updated ${data.updated} (skipped ${data.skipped})`);
      setPreview(null);
      onConfirmed?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Write failed");
    } finally {
      setIsConfirming(false);
    }
  };

  const handleRollback = async () => {
    if (!window.confirm("Delete every material row created by this extractor? Manually-entered materials are safe.")) return;
    setIsRolling(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/materials/extract-from-invoices/rollback`, {}, authHeaders
      );
      toast.success(`Rolled back ${data.deleted} extracted materials`);
      setPreview(null);
      onConfirmed?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rollback failed");
    } finally {
      setIsRolling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="extract-materials-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-700" />
            Extract Materials from Invoices
          </DialogTitle>
          <DialogDescription>
            Scans every paid invoice (all years). Hybrid classifier: keyword heuristics first, then Gemini 3 Flash on anything unclassified.
            Suppliers are auto-assigned when confidence ≥ 90%. Confirmed rows land in the Materials Tracker dated to the invoice's own date — so the Apr-6-2026 tax-year cutoff in AI Analytics applies automatically.
          </DialogDescription>
        </DialogHeader>

        {!preview && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 py-16">
            <AlertCircle className="h-10 w-10 text-amber-500" />
            <p className="text-sm text-muted-foreground max-w-md text-center">
              Click <strong>Scan Invoices</strong> to start. Nothing is written until you review and confirm on the next screen.
            </p>
            <Button
              onClick={handleScan}
              disabled={isScanning}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="extract-scan-btn"
            >
              {isScanning ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scanning…</> : <><Sparkles className="h-4 w-4 mr-2" /> Scan Invoices</>}
            </Button>
          </div>
        )}

        {preview && (
          <>
            <div className="flex flex-wrap items-center gap-3 border-b pb-3 text-sm">
              <Badge variant="outline" data-testid="extract-summary-invoices">
                {preview.total_invoices_scanned} invoices scanned
              </Badge>
              <Badge variant="outline" data-testid="extract-summary-lines">
                {preview.total_lines_scanned} lines read
              </Badge>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-900" data-testid="extract-summary-heuristic">
                {preview.heuristic_hits} keyword matches
              </Badge>
              <Badge variant="outline" className="bg-sky-50 text-sky-900" data-testid="extract-summary-ai">
                {preview.ai_classified} AI-classified
              </Badge>
              <div className="flex-1" />
              <Badge className="bg-amber-600 hover:bg-amber-700">
                {selectedCount} selected · £{selectedValue.toFixed(2)}
              </Badge>
            </div>

            <div className="flex-1 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-center">Conf.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.material_proposals.map((p) => {
                    const key = `${p.invoice_id}:${p.line_index}`;
                    const confPct = Math.round((Number(p.confidence) || 0) * 100);
                    const confColor = confPct >= 90 ? "text-emerald-600" : confPct >= 70 ? "text-amber-600" : "text-red-500";
                    return (
                      <TableRow key={key} data-testid={`extract-row-${key}`}>
                        <TableCell>
                          <Checkbox
                            checked={!!p.selected}
                            onCheckedChange={() => toggleSelected(key)}
                            data-testid={`extract-checkbox-${key}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs">{p.invoice_reference || '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{(p.invoice_date || '').slice(0, 10)}</TableCell>
                        <TableCell className="text-xs max-w-[140px] truncate">{p.customer_name || '—'}</TableCell>
                        <TableCell className="text-sm max-w-[260px]">{p.description}</TableCell>
                        <TableCell>
                          <Input
                            value={p.supplier || ''}
                            onChange={(e) => updateSupplier(key, e.target.value)}
                            placeholder="—"
                            className="h-7 text-xs w-36"
                            data-testid={`extract-supplier-${key}`}
                          />
                        </TableCell>
                        <TableCell className="text-right text-xs">{p.quantity}</TableCell>
                        <TableCell className="text-right font-medium">£{Number(p.total).toFixed(2)}</TableCell>
                        <TableCell className={`text-center text-xs font-semibold ${confColor}`}>{confPct}%</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <DialogFooter className="flex-shrink-0 border-t pt-3">
              <Button
                variant="ghost"
                onClick={handleRollback}
                disabled={isRolling}
                className="mr-auto text-red-600 hover:text-red-700"
                data-testid="extract-rollback-btn"
              >
                {isRolling ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Undo2 className="h-4 w-4 mr-1" />}
                Rollback previous run
              </Button>
              <Button variant="outline" onClick={() => setPreview(null)}>Scan Again</Button>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                onClick={handleConfirm}
                disabled={isConfirming || selectedCount === 0}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="extract-confirm-btn"
              >
                {isConfirming ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Writing…</> : <><CheckCircle2 className="h-4 w-4 mr-2" /> Confirm {selectedCount} material{selectedCount === 1 ? '' : 's'}</>}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExtractMaterialsDialog;
